import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Users, Briefcase, StickyNote, ClipboardCheck, Upload, FileText, Plus, Trash2, Search, Calendar, Brain, Loader2, BarChart3, GraduationCap, Eye, ChevronRight, Pencil, Save, X, FileUp, Hammer, Route, UserPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PageHeader from "../components/PageHeader";

export default function AdminPage({ section = "clients" }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  const [services, setServices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [audits, setAudits] = useState([]);
  const [roadmaps, setRoadmaps] = useState([]);
  const [fecFiles, setFecFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [accompaniments, setAccompaniments] = useState([]);
  const [formations, setFormations] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisType, setAnalysisType] = useState("classical");
  const [accPhases, setAccPhases] = useState([]);

  // Formation detail/edit
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [formationDocs, setFormationDocs] = useState([]);
  const [uploadingFormDoc, setUploadingFormDoc] = useState(false);

  // Audit edit
  const [editingAudit, setEditingAudit] = useState(null);
  const [auditForm, setAuditForm] = useState({});

  // Admin doc upload
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [uploadDocForm, setUploadDocForm] = useState({ client_id: "", title: "" });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Catalog-based assignment (services)
  const [catalogServices, setCatalogServices] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [showAssignService, setShowAssignService] = useState(false);
  const [assignSvcForm, setAssignSvcForm] = useState({ client_id: "", catalog_id: "", intake_template_id: "" });

  // Catalog-based assignment (accompagnements)
  const [accTemplates, setAccTemplates] = useState([]);
  const [showAssignAcc, setShowAssignAcc] = useState(false);
  const [assignAccForm, setAssignAccForm] = useState({ client_id: "", template_id: "" });

  const loadClients = async () => {
    try { const res = await api.get("/admin/clients"); setClients(res.data); } catch {}
  };

  const loadSection = async () => {
    try {
      if (section === "services") {
        const [r, catR, dtR] = await Promise.all([
          api.get("/services"),
          api.get("/catalog/services"),
          api.get("/catalog/document-templates"),
        ]);
        setServices(r.data);
        setCatalogServices(catR.data);
        setDocTemplates(dtR.data);
      }
      if (section === "notes") { const r = await api.get("/notes"); setNotes(r.data); }
      if (section === "audits") { const r = await api.get("/audits"); setAudits(r.data); }
      if (section === "roadmaps") { const r = await api.get("/roadmaps"); setRoadmaps(r.data); }
      if (section === "fec") { const r = await api.get("/fec/files"); setFecFiles(r.data); }
      if (section === "documents") { const r = await api.get("/documents"); setDocuments(r.data); }
      if (section === "accompagnements") {
        const [r, atR] = await Promise.all([
          api.get("/accompaniments"),
          api.get("/accompaniment-templates"),
        ]);
        setAccompaniments(r.data);
        setAccTemplates(atR.data);
      }
      if (section === "formations") { const r = await api.get("/formations"); setFormations(r.data); }
    } catch {}
    setLoading(false);
  };

  useEffect(() => { setLoading(true); setSelectedFormation(null); setEditingAudit(null); loadClients(); loadSection(); }, [section]);

  const openCreate = (type) => {
    setDialogType(type);
    setForm({});
    setEditingItem(null);
    if (type === "accompaniment") {
      setAccPhases([{ name: "Phase 1 : Audit Initial", period: "Semaine 1-2", duration: "2 semaines", deadline: "", description: "", tasks: [
        { id: `t_${Date.now()}_1`, label: "Audit financier express", responsible: "Blitzz", deadline: "", status: "todo" },
        { id: `t_${Date.now()}_2`, label: "Diagnostic commercial", responsible: "Blitzz", deadline: "", status: "todo" },
      ]}]);
    }
    setShowDialog(true);
  };

  const handleCreate = async () => {
    try {
      if (dialogType === "note") {
        await api.post("/notes", { client_id: form.client_id, title: form.title, content: form.content || "", category: form.category || "general" });
      } else if (dialogType === "audit") {
        if (editingItem) {
          await api.put(`/audits/${editingItem}`, { title: form.title, content: form.content || "", status: form.status || "en_cours", recommendations: form.recommendations ? form.recommendations.split("\n").filter(Boolean) : [] });
        } else {
          await api.post("/audits", { client_id: form.client_id, title: form.title, content: form.content || "", status: form.status || "en_cours", recommendations: form.recommendations ? form.recommendations.split("\n").filter(Boolean) : [] });
        }
      } else if (dialogType === "formation") {
        if (editingItem) {
          await api.put(`/formations/${editingItem}`, {
            title: form.title, code: form.code || "", description: form.description || "", program: form.program || "",
            duration: form.duration || "", price: form.price || "",
            qualiopi_indicators: form.qualiopi_indicators ? form.qualiopi_indicators.split("\n").filter(Boolean) : [],
            objectives: form.objectives ? form.objectives.split("\n").filter(Boolean) : [],
            target_audience: form.target_audience || "", prerequisites: form.prerequisites || "", evaluation_methods: form.evaluation_methods || "",
          });
        } else {
          await api.post("/formations", {
            title: form.title, code: form.code || "", description: form.description || "", program: form.program || "",
            duration: form.duration || "", price: form.price || "",
            qualiopi_indicators: form.qualiopi_indicators ? form.qualiopi_indicators.split("\n").filter(Boolean) : [],
            objectives: form.objectives ? form.objectives.split("\n").filter(Boolean) : [],
            target_audience: form.target_audience || "", prerequisites: form.prerequisites || "", evaluation_methods: form.evaluation_methods || "",
          });
        }
      }
      setShowDialog(false);
      setEditingItem(null);
      loadSection();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === "service") await api.delete(`/services/${id}`);
      if (type === "note") await api.delete(`/notes/${id}`);
      if (type === "audit") await api.delete(`/audits/${id}`);
      if (type === "document") await api.delete(`/documents/${id}`);
      if (type === "accompaniment") await api.delete(`/accompaniments/${id}`);
      if (type === "formation") await api.delete(`/formations/${id}`);
      if (type === "fec") await api.delete(`/fec/${id}`);
      loadSection();
    } catch {}
  };

  const handleAnalyze = async (fileId) => {
    setAnalyzing(fileId); setAnalysisResult(null);
    try { const res = await api.post("/fec/analyze", { file_id: fileId, analysis_type: analysisType }); setAnalysisResult(res.data); loadSection(); } catch {}
    setAnalyzing(null);
  };

  // Edit audit
  const openEditAudit = (audit) => {
    setEditingItem(audit.audit_id);
    setDialogType("audit");
    setForm({
      client_id: audit.client_id,
      title: audit.title,
      content: audit.content,
      status: audit.status,
      recommendations: (audit.recommendations || []).join("\n")
    });
    setShowDialog(true);
  };

  // Edit formation
  const openEditFormation = (f) => {
    setEditingItem(f.formation_id);
    setDialogType("formation");
    setForm({
      title: f.title, code: f.code || "", description: f.description || "", program: f.program || "",
      duration: f.duration || "", price: f.price || "",
      qualiopi_indicators: (f.qualiopi_indicators || []).join("\n"),
      objectives: (f.objectives || []).join("\n"),
      target_audience: f.target_audience || "", prerequisites: f.prerequisites || "", evaluation_methods: f.evaluation_methods || "",
    });
    setShowDialog(true);
  };

  // Formation detail with docs
  const openFormationDetail = async (f) => {
    setSelectedFormation(f);
    try {
      const r = await api.get(`/formations/${f.formation_id}/documents`);
      setFormationDocs(r.data);
    } catch { setFormationDocs([]); }
  };

  const handleFormationDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedFormation) return;
    setUploadingFormDoc(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/formations/${selectedFormation.formation_id}/documents?title=${encodeURIComponent(file.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.get(`/formations/${selectedFormation.formation_id}/documents`);
      setFormationDocs(r.data);
    } catch (err) { console.error(err); }
    setUploadingFormDoc(false);
    e.target.value = "";
  };

  const deleteFormationDoc = async (docId) => {
    await api.delete(`/formations/${selectedFormation.formation_id}/documents/${docId}`);
    const r = await api.get(`/formations/${selectedFormation.formation_id}/documents`);
    setFormationDocs(r.data);
  };

  // Admin doc upload
  const handleAdminDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadDocForm.client_id) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/documents/upload?client_id=${uploadDocForm.client_id}&title=${encodeURIComponent(uploadDocForm.title || file.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShowUploadDoc(false);
      setUploadDocForm({ client_id: "", title: "" });
      loadSection();
    } catch (err) { console.error(err); }
    setUploadingDoc(false);
  };

  // Assign service from catalog to client
  const handleAssignService = async () => {
    if (!assignSvcForm.client_id || !assignSvcForm.catalog_id) return;
    const cat = catalogServices.find(c => c.catalog_id === assignSvcForm.catalog_id);
    if (!cat) return;
    try {
      await api.post("/services", {
        client_id: assignSvcForm.client_id,
        name: cat.name,
        description: cat.description || "",
        category: cat.category || "",
        price: cat.price || cat.base_price || "",
        catalog_service_id: cat.catalog_id,
        intake_form_template_id: assignSvcForm.intake_template_id || "",
      });
      setShowAssignService(false);
      setAssignSvcForm({ client_id: "", catalog_id: "", intake_template_id: "" });
      loadSection();
    } catch (e) { console.error(e); }
  };

  // Assign accompaniment from template to client
  const handleAssignAcc = async () => {
    if (!assignAccForm.client_id || !assignAccForm.template_id) return;
    const tpl = accTemplates.find(t => t.template_id === assignAccForm.template_id);
    if (!tpl) return;
    try {
      await api.post("/accompaniments", {
        client_id: assignAccForm.client_id,
        title: tpl.title || tpl.name || "Accompagnement",
        description: tpl.description || "",
        formula: tpl.formula || "START",
        price: tpl.price || "",
        duration_months: tpl.duration_months || 0,
        phases: tpl.phases || [],
        template_id: tpl.template_id,
      });
      setShowAssignAcc(false);
      setAssignAccForm({ client_id: "", template_id: "" });
      loadSection();
    } catch (e) { console.error(e); }
  };

  const ClientSelect = () => (
    <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v })}>
      <SelectTrigger className="bg-[#0b0811] border-white/10 text-white" data-testid="admin-client-select"><SelectValue placeholder="Selectionner un client..." /></SelectTrigger>
      <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
        {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const clientName = (uid) => { const c = clients.find(cl => cl.user_id === uid); return c ? (c.name || c.email) : uid; };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  const sectionTitles = { clients: "Clients", services: "Services", notes: "Notes", audits: "Audits", roadmaps: "Feuilles de Route", fec: "Analyse FEC", documents: "Documents", accompagnements: "Accompagnements", formations: "Formations" };
  const sectionIcons = { clients: <Users />, services: <Briefcase />, notes: <StickyNote />, audits: <ClipboardCheck />, roadmaps: <Route />, fec: <BarChart3 />, documents: <FileText />, accompagnements: <Briefcase />, formations: <GraduationCap /> };
  const createBtnTypes = { notes: "note", audits: "audit", formations: "formation" };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid={`admin-${section}-page`}>
      <PageHeader
        icon={sectionIcons[section]}
        title={sectionTitles[section]}
        description={`Gérez les ${sectionTitles[section].toLowerCase()}`}
        breadcrumb={[{ label: "Admin" }, { label: sectionTitles[section] }]}
        testId={`admin-${section}-header`}
        actions={(
          <>
            {section === "documents" && (
              <Button onClick={() => setShowUploadDoc(true)} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="admin-upload-doc-btn">
                <FileUp className="w-4 h-4 mr-2" /> Upload document
              </Button>
            )}
            {section === "services" && (
              <>
                <Button onClick={() => navigate("/admin/builder?tab=services")} variant="outline" className="border-white/10 text-white hover:bg-white/5 font-bold" data-testid="admin-goto-builder-services-btn">
                  <Hammer className="w-4 h-4 mr-2" /> Créer dans le Builder
                </Button>
                <Button onClick={() => { setAssignSvcForm({ client_id: "", catalog_id: "", intake_template_id: "" }); setShowAssignService(true); }} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="admin-assign-service-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Attribuer à un client
                </Button>
              </>
            )}
            {section === "accompagnements" && (
              <>
                <Button onClick={() => navigate("/admin/builder?tab=acc_templates")} variant="outline" className="border-white/10 text-white hover:bg-white/5 font-bold" data-testid="admin-goto-builder-acc-btn">
                  <Hammer className="w-4 h-4 mr-2" /> Créer dans le Builder
                </Button>
                <Button onClick={() => { setAssignAccForm({ client_id: "", template_id: "" }); setShowAssignAcc(true); }} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="admin-assign-acc-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Attribuer à un client
                </Button>
              </>
            )}
            {createBtnTypes[section] && (
              <Button data-testid={`admin-create-${section}-btn`} onClick={() => openCreate(createBtnTypes[section])} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">
                <Plus className="w-4 h-4 mr-2" /> Créer
              </Button>
            )}
          </>
        )}
      />

      {section !== "clients" && (
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888194]" />
        <Input data-testid="admin-search" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-[#888194]/60 focus:border-[#f7c935]/50 focus:ring-1 focus:ring-[#f7c935]/20" />
      </div>
      )}

      {/* Clients */}
      {section === "clients" && (
        <div className="bg-[#14101c] border border-white/[0.06] rounded-xl flex flex-col" style={{ height: "calc(100vh - 10rem)" }} data-testid="clients-panel">
          {/* Glass filter bar */}
          <div className="px-5 py-3 border-b border-white/[0.06] bg-[#0b0811]/60 backdrop-blur-xl flex flex-wrap items-center gap-3 flex-shrink-0 rounded-t-xl">
            <div className="flex items-center gap-2 mr-auto">
              <Users className="w-4 h-4 text-[#f7c935]" />
              <span className="text-sm font-heading font-semibold text-white">{clients.length} Clients</span>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[#888194]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher nom, email, societe..."
                className="pl-8 pr-3 py-1.5 bg-black/20 border border-white/10 rounded-lg text-xs text-white placeholder:text-[#888194]/60 w-64 focus:border-[#f7c935]/50 focus:ring-1 focus:ring-[#f7c935]/20 outline-none transition-all" data-testid="clients-search" />
            </div>
            <select value={search.startsWith("__company:") ? search : ""} onChange={e => setSearch(e.target.value)}
              className="bg-black/20 border border-white/10 rounded-lg text-xs text-[#d1cedb] px-2.5 py-1.5 focus:border-[#f7c935]/50 outline-none" data-testid="clients-filter-company">
              <option value="">Toutes societes</option>
              {[...new Set(clients.map(c => c.company_name).filter(Boolean))].sort().map(cn => (
                <option key={cn} value={cn}>{cn}</option>
              ))}
            </select>
            <Button onClick={() => { setDialogType("client"); setForm({}); setShowDialog(true); }} size="sm" className="bg-[#f7c935] text-[#0b0811] hover:bg-[#f7c935]/90 font-bold text-xs h-8" data-testid="create-client-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Client
            </Button>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-left min-w-max">
              <thead>
                <tr className="border-b border-white/[0.06] sticky top-0 bg-[#14101c] z-10">
                  <th className="px-5 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 200 }}>Client</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 160 }}>Societe</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 180 }}>Email</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 120 }}>Telephone</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 100 }}>SIREN</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 120 }}>Services actifs</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: 130 }}>Mis a jour</th>
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider w-16"></th>
                </tr>
              </thead>
              <tbody>
                {clients
                  .filter(c => {
                    const s = search.toLowerCase();
                    if (!s) return true;
                    return (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.company_name || "").toLowerCase().includes(s) || (c.phone || "").includes(s) || (c.siren || "").includes(s);
                  })
                  .map((client, i) => (
                  <tr key={client.user_id}
                    onClick={() => navigate(`/admin/clients/${client.user_id}`)}
                    className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer transition-colors duration-150 group relative"
                    data-testid={`client-row-${client.user_id}`}
                    data-href={`/admin/clients/${client.user_id}`}>
                    <td className="px-5 py-3.5">
                      <Link
                        to={`/admin/clients/${client.user_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-0 z-10"
                        aria-label={`Voir ${client.name || "client"}`}
                        data-testid={`client-row-link-${client.user_id}`}
                      />
                      <div className="flex items-center gap-3 relative z-20 pointer-events-none">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
                          style={{ background: "#461d8a20", color: "#f7c935", border: "1px solid #461d8a30" }}>
                          {(client.name || client.email)?.[0]?.toUpperCase() || "C"}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">{client.name || "Sans nom"}</div>
                          {client.billing_email && <div className="text-[9px] text-[#888194]">Fact: {client.billing_email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-[#d1cedb]">{client.company_name || <span className="text-[#888194]/40">-</span>}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-[#d1cedb] truncate max-w-[170px] inline-block">{client.email}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-[#888194]">{client.phone || "-"}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      {client.siren ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#f7c935]/8 text-[#f7c935] border border-[#f7c935]/15 font-mono">{client.siren}</span>
                      ) : <span className="text-[10px] text-[#888194]/40">-</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        {(client.active_accompaniments > 0 || client.active_services > 0) ? (
                          <>
                            {client.active_accompaniments > 0 && <span className="text-[10px] px-2 py-0.5 rounded-md bg-green-500/10 text-green-400 border border-green-500/15">{client.active_accompaniments} acc.</span>}
                            {client.active_services > 0 && <span className="text-[10px] px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/15">{client.active_services} svc.</span>}
                          </>
                        ) : <span className="text-[10px] text-[#888194]/40">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-[10px] text-[#888194]">{client.updated_at ? new Date(client.updated_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" }) : "-"}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <ChevronRight className="w-4 h-4 text-[#888194]/40 group-hover:text-[#f7c935] transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clients.filter(c => (c.name || c.email || "").toLowerCase().includes(search.toLowerCase())).length === 0 && (
              <div className="text-center py-16">
                <Users className="w-10 h-10 mx-auto mb-3 text-[#461d8a]" />
                <p className="text-sm text-[#888194]">Aucun client trouve</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Services */}
      {section === "services" && (
        <div className="space-y-3">
          {catalogServices.length > 0 && (
            <div className="bg-[#461d8a]/10 border border-[#461d8a]/20 rounded-xl p-4 flex items-center gap-3 mb-2" data-testid="services-catalog-info">
              <Hammer className="w-5 h-5 text-[#5a2d9c] flex-shrink-0" />
              <div className="text-sm text-slate-300">
                <strong className="text-white">{catalogServices.length}</strong> services dans le catalogue Builder.
                Utilisez <strong style={{ color: "#f7c935" }}>"Attribuer a un client"</strong> pour assigner un service du catalogue.
              </div>
            </div>
          )}
          {services.filter(s => s.name.toLowerCase().includes(search.toLowerCase())).map((srv, i) => (
            <div key={srv.service_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }}>
              <div>
                <div className="font-semibold">{srv.name}</div>
                <div className="text-sm text-slate-500">{srv.description} {srv.category && `- ${srv.category}`}</div>
                <div className="text-xs text-slate-600 mt-1">Client: {clientName(srv.client_id)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${srv.status === "active" ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400"}`}>{srv.status === "active" ? "Actif" : srv.status}</span>
                <button onClick={() => handleDelete("service", srv.service_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notes */}
      {section === "notes" && (
        <div className="space-y-3">
          {notes.filter(n => n.title.toLowerCase().includes(search.toLowerCase())).map((note, i) => (
            <div key={note.note_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }}>
              <div><div className="font-semibold">{note.title}</div><div className="text-sm text-slate-500 line-clamp-1">{note.content}</div><div className="text-xs text-slate-600 mt-1">Client: {clientName(note.client_id)}</div></div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-600">{new Date(note.created_at).toLocaleDateString("fr-FR")}</span>
                <button onClick={() => handleDelete("note", note.note_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audits with edit/delete */}
      {section === "audits" && (
        <div className="space-y-3">
          {audits.filter(a => a.title.toLowerCase().includes(search.toLowerCase())).map((audit, i) => (
            <div key={audit.audit_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`admin-audit-${audit.audit_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{audit.title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">Client: {clientName(audit.client_id)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${audit.status === "termine" ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>
                    {audit.status === "termine" ? "Termine" : "En cours"}
                  </span>
                  <button onClick={() => openEditAudit(audit)} className="p-1.5 rounded hover:bg-white/10" data-testid={`edit-audit-${audit.audit_id}`}>
                    <Pencil className="w-4 h-4 text-slate-400" />
                  </button>
                  <button onClick={() => handleDelete("audit", audit.audit_id)} className="p-1.5 rounded hover:bg-red-500/10" data-testid={`delete-audit-${audit.audit_id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-slate-500 mt-2 line-clamp-2">{audit.content}</div>
              {audit.recommendations?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-xs font-bold text-slate-500 mb-1">Recommandations :</div>
                  <ul className="space-y-1">{audit.recommendations.slice(0, 3).map((r, j) => (
                    <li key={j} className="text-xs text-slate-500 flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#f7c935" }} />{r}
                    </li>
                  ))}</ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* FEC */}
      {section === "fec" && (
        <div>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm font-medium text-slate-400">Type d'analyse :</span>
            <Select value={analysisType} onValueChange={setAnalysisType}>
              <SelectTrigger className="w-[220px] bg-[#0b0811] border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14101c] border-white/10 text-white">
                <SelectItem value="classical"><div className="flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Classique</div></SelectItem>
                <SelectItem value="ai"><div className="flex items-center gap-2"><Brain className="w-4 h-4" /> IA (GPT-5.2)</div></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3 mb-8">
            {fecFiles.map((f, i) => (
              <div key={f.file_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }}>
                <div><div className="font-semibold">{f.original_filename}</div><div className="text-sm text-slate-500">{(f.size/1024).toFixed(1)} KB - Client: {clientName(f.user_id)}</div></div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${f.status === "analyzed" ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>{f.status === "analyzed" ? "Analyse" : "Uploade"}</span>
                  <Button onClick={() => handleAnalyze(f.file_id)} disabled={analyzing === f.file_id} size="sm" className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold text-xs">
                    {analyzing === f.file_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Analyser"}
                  </Button>
                  {f.status === "analyzed" && f.analysis && <Button size="sm" variant="outline" onClick={() => setAnalysisResult(f.analysis)} className="border-white/10 text-white hover:bg-white/5 text-xs">Voir</Button>}
                  <button onClick={() => handleDelete("fec", f.file_id)} className="p-1.5 rounded hover:bg-red-500/10" data-testid={`delete-fec-${f.file_id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {analysisResult && (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Resultats</h3>
                <button onClick={() => setAnalysisResult(null)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4 text-slate-400" /></button>
              </div>
              <p className="text-sm text-slate-400 mb-4">{analysisResult.summary}</p>
              {analysisResult.kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {analysisResult.kpis.chiffre_affaires !== undefined && <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4"><div className="text-xs text-slate-500 mb-1">CA</div><div className="text-xl font-black" style={{ color: "#f7c935" }}>{analysisResult.kpis.chiffre_affaires?.toLocaleString("fr-FR")} EUR</div></div>}
                  {analysisResult.kpis.charges !== undefined && <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4"><div className="text-xs text-slate-500 mb-1">Charges</div><div className="text-xl font-black text-red-400">{analysisResult.kpis.charges?.toLocaleString("fr-FR")} EUR</div></div>}
                  {analysisResult.kpis.resultat !== undefined && <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4"><div className="text-xs text-slate-500 mb-1">Resultat</div><div className={`text-xl font-black ${analysisResult.kpis.resultat >= 0 ? "text-green-400" : "text-red-400"}`}>{analysisResult.kpis.resultat?.toLocaleString("fr-FR")} EUR</div></div>}
                  {analysisResult.kpis.marge_nette !== undefined && <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4"><div className="text-xs text-slate-500 mb-1">Marge</div><div className="text-xl font-black" style={{ color: "#5a2d9c" }}>{analysisResult.kpis.marge_nette} %</div></div>}
                </div>
              )}
              {analysisResult.ai_analysis && <div className="bg-[#0b0811] border border-[#461d8a]/30 rounded-lg p-6"><div className="flex items-center gap-2 mb-3"><Brain className="w-5 h-5" style={{ color: "#f7c935" }} /><h4 className="font-bold">Analyse IA</h4></div><div className="text-sm text-slate-300 whitespace-pre-wrap">{analysisResult.ai_analysis}</div></div>}
            </div>
          )}
        </div>
      )}

      {/* Documents with upload button */}
      {section === "documents" && (
        <div className="space-y-3">
          {documents.filter(d => (d.title || "").toLowerCase().includes(search.toLowerCase())).map((doc, i) => (
            <div key={doc.document_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }}>
              <div><div className="font-semibold">{doc.title}</div><div className="text-sm text-slate-500">{doc.doc_type} - Client: {clientName(doc.client_id)}</div></div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${doc.status === "soumis" ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>{doc.status === "soumis" ? "Soumis" : "Brouillon"}</span>
                <button onClick={() => handleDelete("document", doc.document_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Accompagnements */}
      {section === "accompagnements" && (
        <div className="space-y-4">
          {accTemplates.length > 0 && (
            <div className="bg-[#461d8a]/10 border border-[#461d8a]/20 rounded-xl p-4 flex items-center gap-3" data-testid="acc-catalog-info">
              <Hammer className="w-5 h-5 text-[#5a2d9c] flex-shrink-0" />
              <div className="text-sm text-slate-300">
                <strong className="text-white">{accTemplates.length}</strong> modeles d'accompagnement dans le Builder.
                Utilisez <strong style={{ color: "#f7c935" }}>"Attribuer a un client"</strong> pour assigner un modele.
              </div>
            </div>
          )}
          {/* Filter */}
          <div className="flex gap-2 mb-2">
            {[{ key: "all", label: "Tous" }, { key: "active", label: "En cours" }, { key: "termine", label: "Termines" }].map(f => (
              <button key={f.key} onClick={() => setSearch(f.key === "all" ? "" : `status:${f.key}`)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${(!search && f.key === "all") || search === `status:${f.key}` ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
                data-testid={`acc-filter-${f.key}`}>
                {f.label}
              </button>
            ))}
          </div>
          {accompaniments.filter(a => {
            if (search.startsWith("status:")) return a.status === search.split(":")[1];
            return a.title.toLowerCase().includes(search.toLowerCase());
          }).map((acc, i) => {
            const totalTasks = acc.phases?.reduce((s, p) => s + (p.tasks?.length || 0), 0) || 0;
            const doneTasks = acc.phases?.reduce((s, p) => s + (p.tasks?.filter(t => t.status === "done").length || 0), 0) || 0;
            const progress = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
            return (
              <div key={acc.accompaniment_id}
                className="w-full bg-[#14101c] border border-white/10 rounded-xl p-5 animate-fade-in-up hover:border-[#f7c935]/30 transition-colors group relative" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`admin-accompaniment-${acc.accompaniment_id}`}>
                <Link
                  to={`/admin/accompagnements/${acc.accompaniment_id}`}
                  className="w-full text-left block"
                  data-testid={`admin-accompaniment-link-${acc.accompaniment_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{acc.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${acc.status === "termine" ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>
                          {acc.status === "termine" ? "Termine" : "En cours"}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5">
                        {acc.formula} - Client: {clientName(acc.client_id)}
                        {acc.price && <span className="ml-2 font-medium" style={{ color: "#f7c935" }}>{acc.price}</span>}
                        {acc.duration_months && <span className="ml-2 text-slate-400">{acc.duration_months} mois</span>}
                      </div>
                      <div className="text-xs text-slate-600 mt-1">{acc.phases?.length || 0} phases - {totalTasks} taches ({doneTasks} terminees) {acc.start_date && `- Debut ${new Date(acc.start_date).toLocaleDateString("fr-FR")}`}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-xl font-black" style={{ color: "#f7c935" }}>{progress}%</div>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </div>
                  </div>
                </Link>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete("accompaniment", acc.accompaniment_id); }}
                  className="absolute top-3 right-3 p-2 rounded hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Supprimer l'accompagnement"
                  data-testid={`delete-accompaniment-${acc.accompaniment_id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Formations with edit/detail/docs */}
      {section === "formations" && !selectedFormation && (
        <div className="space-y-3">
          {formations.filter(f => f.title.toLowerCase().includes(search.toLowerCase())).map((f, i) => (
            <div key={f.formation_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`admin-formation-${f.formation_id}`}>
              <div className="flex-1 cursor-pointer" onClick={() => openFormationDetail(f)}>
                <div className="font-semibold">{f.title}</div>
                <div className="text-sm text-slate-500">{f.code} - {f.duration} {f.price && `- ${f.price}`}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded bg-[#461d8a]/20 text-[#5a2d9c] font-medium">Qualiopi</span>
                <button onClick={() => openEditFormation(f)} className="p-1.5 rounded hover:bg-white/10" data-testid={`edit-formation-${f.formation_id}`}>
                  <Pencil className="w-4 h-4 text-slate-400" />
                </button>
                <button onClick={() => handleDelete("formation", f.formation_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formation detail panel */}
      {section === "formations" && selectedFormation && (
        <div>
          <button onClick={() => setSelectedFormation(null)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4 transition-colors">
            <ChevronRight className="w-4 h-4 rotate-180" /> Retour a la liste
          </button>
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-black">{selectedFormation.title}</h2>
                <div className="text-sm text-slate-500 mt-1">{selectedFormation.code} - {selectedFormation.duration} {selectedFormation.price && `- ${selectedFormation.price}`}</div>
              </div>
              <Button onClick={() => openEditFormation(selectedFormation)} variant="outline" className="border-white/10 text-white hover:bg-white/5">
                <Pencil className="w-4 h-4 mr-2" /> Modifier
              </Button>
            </div>
            {selectedFormation.description && <p className="text-sm text-slate-300 mb-4">{selectedFormation.description}</p>}
            {selectedFormation.program && <div className="mb-4"><h4 className="text-sm font-bold mb-1" style={{ color: "#f7c935" }}>Programme</h4><p className="text-sm text-slate-400 whitespace-pre-wrap">{selectedFormation.program}</p></div>}
            {selectedFormation.objectives?.length > 0 && <div className="mb-4"><h4 className="text-sm font-bold mb-1" style={{ color: "#f7c935" }}>Objectifs</h4><ul className="space-y-1">{selectedFormation.objectives.map((o, j) => <li key={j} className="text-sm text-slate-400 flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#f7c935" }} />{o}</li>)}</ul></div>}
            {selectedFormation.target_audience && <div className="mb-4"><h4 className="text-sm font-bold mb-1" style={{ color: "#f7c935" }}>Public cible</h4><p className="text-sm text-slate-400">{selectedFormation.target_audience}</p></div>}
          </div>

          {/* Formation documents */}
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5" style={{ color: "#f7c935" }} /> Documents de la formation</h3>
              <label className="cursor-pointer">
                <input type="file" className="hidden" onChange={handleFormationDocUpload} />
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f7c935] text-[#14101c] font-bold text-sm hover:bg-[#e6b825] transition-colors">
                  {uploadingFormDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                  Ajouter un document
                </div>
              </label>
            </div>
            {formationDocs.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">Aucun document pour cette formation.</p>
            ) : (
              <div className="space-y-2">
                {formationDocs.map(d => (
                  <div key={d.doc_id} className="flex items-center justify-between p-3 rounded-lg bg-[#0b0811] border border-white/5">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div>
                        <div className="text-sm font-medium">{d.title || d.filename}</div>
                        <div className="text-xs text-slate-600">{d.filename} - {(d.size / 1024).toFixed(1)} KB</div>
                      </div>
                    </div>
                    <button onClick={() => deleteFormationDoc(d.doc_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) setEditingItem(null); }}>
        <DialogContent className={`bg-[#14101c] border-white/10 text-white ${dialogType === "formation" ? "max-w-3xl max-h-[85vh] overflow-y-auto" : "max-w-lg"}`}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {dialogType === "note" && "Nouvelle note"}
              {dialogType === "audit" && (editingItem ? "Modifier l'audit" : "Nouvel audit")}
              {dialogType === "formation" && (editingItem ? "Modifier la formation" : "Nouvelle formation")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {dialogType !== "formation" && !editingItem && <div><label className="text-sm text-slate-400 mb-1.5 block">Client</label><ClientSelect /></div>}

            {/* Note */}
            {dialogType === "note" && (<>
              <Input placeholder="Titre" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="admin-note-title" />
              <Textarea placeholder="Contenu" value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            </>)}

            {dialogType === "audit" && (<>
              <Input placeholder="Titre" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="admin-audit-title" />
              <Textarea placeholder="Contenu" value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Select value={form.status || "en_cours"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  <SelectItem value="en_cours">En cours</SelectItem>
                  <SelectItem value="termine">Termine</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                </SelectContent>
              </Select>
              <Textarea placeholder="Recommandations (une par ligne)" value={form.recommendations || ""} onChange={(e) => setForm({ ...form, recommendations: e.target.value })} rows={3} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            </>)}

            {/* Formation */}
            {dialogType === "formation" && (<>
              <Input placeholder="Titre de la formation" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="admin-formation-title" />
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Code (ex: F01)" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
                <Input placeholder="Duree" value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
                <Input placeholder="Prix" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              </div>
              <Textarea placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Textarea placeholder="Programme detaille" value={form.program || ""} onChange={(e) => setForm({ ...form, program: e.target.value })} rows={4} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Textarea placeholder="Objectifs (un par ligne)" value={form.objectives || ""} onChange={(e) => setForm({ ...form, objectives: e.target.value })} rows={3} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Input placeholder="Public cible" value={form.target_audience || ""} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Input placeholder="Prerequis" value={form.prerequisites || ""} onChange={(e) => setForm({ ...form, prerequisites: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Textarea placeholder="Methodes d'evaluation" value={form.evaluation_methods || ""} onChange={(e) => setForm({ ...form, evaluation_methods: e.target.value })} rows={2} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Textarea placeholder="Indicateurs Qualiopi (un par ligne)" value={form.qualiopi_indicators || ""} onChange={(e) => setForm({ ...form, qualiopi_indicators: e.target.value })} rows={2} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            </>)}

            <Button data-testid="admin-create-submit-btn" onClick={handleCreate} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">
              {editingItem ? "Enregistrer les modifications" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Document Upload Dialog */}
      <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Upload un document</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-slate-400 mb-1.5 block">Client</label>
              <Select value={uploadDocForm.client_id} onValueChange={(v) => setUploadDocForm({ ...uploadDocForm, client_id: v })}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue placeholder="Selectionner un client..." /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
                  {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Titre du document" value={uploadDocForm.title} onChange={(e) => setUploadDocForm({ ...uploadDocForm, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            <label className="cursor-pointer block">
              <input type="file" className="hidden" onChange={handleAdminDocUpload} disabled={!uploadDocForm.client_id} />
              <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed font-bold text-sm transition-colors ${uploadDocForm.client_id ? "border-[#f7c935]/50 text-[#f7c935] hover:bg-[#f7c935]/5 cursor-pointer" : "border-white/10 text-slate-600 cursor-not-allowed"}`}>
                {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                Choisir un fichier
              </div>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Service from Catalog Dialog */}
      <Dialog open={showAssignService} onOpenChange={setShowAssignService}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Attribuer un service a un client</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-xs text-slate-500">Selectionnez un service du catalogue Builder et attribuez-le a un client.</p>
            <div>
              <label className="text-sm text-slate-400 mb-1.5 block">Client</label>
              <Select value={assignSvcForm.client_id} onValueChange={(v) => setAssignSvcForm({ ...assignSvcForm, client_id: v })}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white" data-testid="assign-svc-client-select"><SelectValue placeholder="Selectionner un client..." /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
                  {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {catalogServices.length === 0 ? (
              <div className="bg-[#161020] border border-white/5 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-400">Aucun service dans le catalogue.</p>
                <p className="text-xs text-slate-500 mt-1">Creez vos services dans le <strong>Builder</strong> d'abord.</p>
                <Button onClick={() => navigate("/admin/builder?tab=services")} size="sm" variant="outline" className="mt-2 border-white/10 text-white hover:bg-white/5">
                  <Hammer className="w-3 h-3 mr-1" /> Aller au Builder
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm text-slate-400 mb-1.5 block">Service du catalogue</label>
                  <select value={assignSvcForm.catalog_id} onChange={e => setAssignSvcForm({ ...assignSvcForm, catalog_id: e.target.value })}
                    className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="assign-svc-catalog-select">
                    <option value="">Choisir un service...</option>
                    {catalogServices.map(cs => (
                      <option key={cs.catalog_id} value={cs.catalog_id}>{cs.name} {cs.category ? `(${cs.category})` : ""}</option>
                    ))}
                  </select>
                </div>
                {assignSvcForm.catalog_id && (() => {
                  const sel = catalogServices.find(c => c.catalog_id === assignSvcForm.catalog_id);
                  return sel ? (
                    <div className="bg-[#161020] rounded-lg p-3 text-sm">
                      <div className="text-slate-300">{sel.description}</div>
                      {(sel.price || sel.base_price) && <div className="mt-1 font-bold" style={{ color: "#f7c935" }}>{sel.price || sel.base_price}</div>}
                    </div>
                  ) : null;
                })()}
                <div>
                  <label className="text-sm text-slate-400 mb-1.5 block">Document a faire remplir par le client</label>
                  <select value={assignSvcForm.intake_template_id} onChange={e => setAssignSvcForm({ ...assignSvcForm, intake_template_id: e.target.value })}
                    className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="assign-svc-intake-select">
                    <option value="">Aucun document requis</option>
                    {docTemplates.map(dt => (
                      <option key={dt.template_id} value={dt.template_id}>{dt.name}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={handleAssignService} disabled={!assignSvcForm.client_id || !assignSvcForm.catalog_id}
                  className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-assign-service-btn">
                  Attribuer le service
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Accompaniment from Template Dialog */}
      <Dialog open={showAssignAcc} onOpenChange={setShowAssignAcc}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Attribuer un accompagnement a un client</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-xs text-slate-500">Selectionnez un modele du Builder et attribuez-le a un client.</p>
            <div>
              <label className="text-sm text-slate-400 mb-1.5 block">Client</label>
              <Select value={assignAccForm.client_id} onValueChange={(v) => setAssignAccForm({ ...assignAccForm, client_id: v })}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white" data-testid="assign-acc-client-select"><SelectValue placeholder="Selectionner un client..." /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
                  {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {accTemplates.length === 0 ? (
              <div className="bg-[#161020] border border-white/5 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-400">Aucun modele d'accompagnement.</p>
                <p className="text-xs text-slate-500 mt-1">Creez des modeles dans le <strong>Builder</strong> d'abord.</p>
                <Button onClick={() => navigate("/admin/builder?tab=acc_templates")} size="sm" variant="outline" className="mt-2 border-white/10 text-white hover:bg-white/5">
                  <Hammer className="w-3 h-3 mr-1" /> Aller au Builder
                </Button>
              </div>
            ) : (
              <>
                <select value={assignAccForm.template_id} onChange={e => setAssignAccForm({ ...assignAccForm, template_id: e.target.value })}
                  className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="assign-acc-template-select">
                  <option value="">Choisir un modele...</option>
                  {accTemplates.map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.title || t.name} {t.formula ? `(${t.formula})` : ""}</option>
                  ))}
                </select>
                {assignAccForm.template_id && (() => {
                  const tpl = accTemplates.find(t => t.template_id === assignAccForm.template_id);
                  return tpl ? (
                    <div className="bg-[#161020] rounded-lg p-3 text-sm space-y-1">
                      <div className="text-slate-300">{tpl.description}</div>
                      <div className="flex gap-4 text-xs text-slate-400">
                        {tpl.formula && <span>Formule : <strong className="text-white">{tpl.formula}</strong></span>}
                        {tpl.duration_months && <span>Duree : <strong className="text-white">{tpl.duration_months} mois</strong></span>}
                        {tpl.price && <span>Prix : <strong style={{ color: "#f7c935" }}>{tpl.price} EUR</strong></span>}
                      </div>
                      {tpl.phases && <div className="text-xs text-slate-500">{tpl.phases.length} phases</div>}
                    </div>
                  ) : null;
                })()}
                <Button onClick={handleAssignAcc} disabled={!assignAccForm.client_id || !assignAccForm.template_id}
                  className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-assign-acc-btn">
                  Attribuer l'accompagnement
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
